/**
 * Entity Linking Service
 * 
 * Provides functionality for creating bidirectional links between entities
 * (Days, Accommodations, Places, Travel, Documents) in the Trip Planner system.
 * 
 * Features:
 * - Check for existing links (prevent duplicates)
 * - Create bidirectional links
 * - Remove links
 * - Query link relationships
 * - Handle different entity type combinations
 */

import {
  getDay,
  getAccommodationById,
  getPlaceById,
  getTravel,
  updateDay,
  updateAccommodation,
  updatePlace,
  updateTravel
} from '../firebase/firestoreService';
import { documentService } from '@trip-planner-monorepo/documents';
import { eventBridgeService, EVENT_TYPES } from './eventService';
import {
  createDayReferenceBlock,
  createPlaceReferenceBlock,
  createTravelReferenceBlock,
  createAccommodationReferenceBlock,
  createDocumentReferenceBlock,
  hasEntityReference,
  findEntityReferences,
  removeEntityReference
} from '../utils/contentBlockUtils';
import { formatDayDisplayLabel } from '../utils/dayDisplayUtils';
import { daysService } from '@trip-planner-monorepo/days';
import { accommodationService } from '@trip-planner-monorepo/accommodation';
import { placesService } from '@trip-planner-monorepo/places';
import { travelService } from '@trip-planner-monorepo/travel';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseInit';
import { connectionRegistryService } from './connectionRegistryService';
import { legacyLinkAdapter } from './legacyLinkAdapter';
import { linkRegistryConfigService } from './linkRegistryConfigService';
import {
  resolveManualLinkType,
  shouldRouteThroughRegistry,
  shouldReadFromRegistry
} from '../utils/linkRegistryModeUtils';
import { getOtherEndpoint, getRelatedEntityModels } from './linkPresentationService';
import { isActiveLink } from '../utils/linkCanonicalUtils';

/**
 * Helper function to get entity by ID
 */
async function getEntityById(userId, tripId, entityType, entityId) {
  try {
    switch (entityType) {
    case 'day':
      return await getDay(userId, tripId, entityId);
    case 'accommodation':
      return await getAccommodationById(userId, tripId, entityId);
    case 'place':
      return await getPlaceById(userId, tripId, entityId);
    case 'travel': {
      const travelRef = doc(db, 'users', userId, 'trips', tripId, 'travel', entityId);
      const travelDoc = await getDoc(travelRef);
      if (travelDoc.exists()) {
        return { id: travelDoc.id, ...travelDoc.data() };
      }
      return null;
    }
    case 'document':
      return await documentService.getDocument(userId, tripId, entityId);
    default:
      throw new Error(`Unsupported entity type: ${entityType}`);
    }
  } catch (error) {
    console.error(`Error getting ${entityType} by ID:`, error);
    throw error;
  }
}

/**
 * Helper function to get content blocks from an entity
 */
function getEntityContentBlocks(entity, entityType) {
  switch (entityType) {
  case 'day':
    return entity.content || [];
  case 'accommodation':
  case 'place':
  case 'travel':
    return entity.contentBlocks || [];
  case 'document':
    // Documents use connections array, not content blocks
    // Return empty array for content blocks check, but we'll handle connections separately
    return [];
  default:
    return [];
  }
}

/**
 * Helper function to check if a document has a connection to an entity
 */
function hasDocumentConnection(document, entityType, entityId) {
  if (!document || !document.connections || !Array.isArray(document.connections)) {
    return false;
  }
  return document.connections.some(
    conn => conn.entityType === entityType && conn.entityId === entityId
  );
}

/**
 * Helper function to update entity content blocks
 */
async function updateEntityContentBlocks(userId, tripId, entityType, entityId, contentBlocks) {
  switch (entityType) {
  case 'day':
    await updateDay(userId, tripId, entityId, { content: contentBlocks });
    break;
  case 'accommodation':
    await updateAccommodation(userId, tripId, entityId, { contentBlocks });
    break;
  case 'place':
    await updatePlace(userId, tripId, entityId, { contentBlocks });
    break;
  case 'travel':
    await updateTravel(userId, tripId, entityId, { contentBlocks });
    break;
  case 'document':
    // Documents handle connections differently - use documentService.addConnection
    // This function is not used for documents, they use addDocumentConnection instead
    throw new Error('Documents should use addDocumentConnection, not updateEntityContentBlocks');
  default:
    throw new Error(`Unsupported entity type for content blocks: ${entityType}`);
  }
}

/**
 * Helper function to add a connection to a document
 */
async function addDocumentConnection(userId, tripId, documentId, sourceEntityType, sourceEntityId, connectionType = 'related') {
  await documentService.addConnection(userId, tripId, documentId, {
    entityType: sourceEntityType,
    entityId: sourceEntityId,
    connectionType: connectionType
  });
}

/**
 * Helper function to create appropriate reference block
 */
function createReferenceBlock(sourceEntityType, targetEntityType, targetEntity, metadata = {}) {
  const targetId = targetEntity.id;
  let targetName = metadata.entityName || targetEntity.summary || targetEntity.name || targetEntity.title;

  if (targetEntityType === 'day') {
    targetName = formatDayDisplayLabel(targetEntity, { fallbackName: targetName || 'Day' });
  } else {
    targetName = targetName || 'Unnamed';
  }

  switch (targetEntityType) {
  case 'day':
    return createDayReferenceBlock(targetId, targetName, targetEntity.date, metadata);
  case 'accommodation':
    return createAccommodationReferenceBlock(targetId, targetName, metadata.context, metadata);
  case 'place':
    return createPlaceReferenceBlock(targetId, targetName, metadata.context, metadata);
  case 'travel':
    return createTravelReferenceBlock(targetId, targetName, targetEntity.type || targetEntity.details?.type, metadata);
  case 'document':
    return createDocumentReferenceBlock(targetEntity, metadata.connectionType || 'related');
  default:
    throw new Error(`Unsupported target entity type: ${targetEntityType}`);
  }
}

function extractLegacyLinksFromContentBlocks(contentBlocks) {
  const { extractEntityReferencesFromContentBlocks } = require('../utils/contentBlockUtils');
  return extractEntityReferencesFromContentBlocks(contentBlocks).map((ref) => ({
    type: ref.entityType,
    id: ref.entityId,
    name: ref.entityName,
    blockId: ref.id
  }));
}

async function resolveRegistryLinkEndpoints(userId, tripId, entityType, entityId) {
  const links = await connectionRegistryService.getLinksForEntity(userId, tripId, entityType, entityId);
  return links
    .filter((link) => isActiveLink(link) && link.linkIntent !== 'rejected' && link.displayMode !== 'hidden')
    .map((link) => {
      const other = getOtherEndpoint(link, entityType, entityId);
      if (!other) {
        return null;
      }
      return {
        type: other.type,
        id: other.id,
        linkId: link.linkId || link.id
      };
    })
    .filter(Boolean);
}

function mergeLinkListings(registryLinks, legacyLinks) {
  const seen = new Set(registryLinks.map((link) => `${link.type}:${link.id}`));
  const merged = [...registryLinks];

  legacyLinks.forEach((link) => {
    const key = `${link.type}:${link.id}`;
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(link);
    }
  });

  return merged;
}

async function checkLegacyContentLinkExists(
  userId,
  tripId,
  sourceEntityType,
  sourceEntityId,
  targetEntityType,
  targetEntityId
) {
  const sourceEntity = await getEntityById(userId, tripId, sourceEntityType, sourceEntityId);
  const targetEntity = await getEntityById(userId, tripId, targetEntityType, targetEntityId);

  if (!sourceEntity || !targetEntity) {
    return { exists: false, direction: null };
  }

  let sourceHasTarget = false;
  if (sourceEntityType === 'document') {
    sourceHasTarget = hasDocumentConnection(sourceEntity, targetEntityType, targetEntityId);
  } else {
    const sourceContentBlocks = getEntityContentBlocks(sourceEntity, sourceEntityType);
    sourceHasTarget = hasEntityReference(sourceContentBlocks, targetEntityType, targetEntityId);
  }

  let targetHasSource = false;
  if (targetEntityType === 'document') {
    targetHasSource = hasDocumentConnection(targetEntity, sourceEntityType, sourceEntityId);
  } else {
    const targetContentBlocks = getEntityContentBlocks(targetEntity, targetEntityType);
    targetHasSource = hasEntityReference(targetContentBlocks, sourceEntityType, sourceEntityId);
  }

  if (sourceHasTarget && targetHasSource) {
    return { exists: true, direction: 'both' };
  }
  if (sourceHasTarget) {
    return { exists: true, direction: 'source->target' };
  }
  if (targetHasSource) {
    return { exists: true, direction: 'target->source' };
  }

  return { exists: false, direction: null };
}

async function resolveEntityLinkListing(userId, tripId, entityType, entityId) {
  const config = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
  let registryLinks = [];

  if (shouldReadFromRegistry(config)) {
    const { source, models } = await getRelatedEntityModels(userId, tripId, entityType, entityId, {
      surface: 'linker'
    });

    if (source === 'registry') {
      registryLinks = models.map((model) => ({
        type: model.entityType,
        id: model.entityId,
        name: model.entityName,
        linkId: model.linkId
      }));
    }
  }

  // Always also check for surviving legacy content-block references — including on
  // registry_only trips (N7). Without this, an entity linked only by an extractor
  // leftover / un-migrated block was still offered as "linkable" in the Link Entities
  // modal, and selecting it created a duplicate associated_with row on top of it.
  const entity = await getEntityById(userId, tripId, entityType, entityId);
  const legacyLinks = entity
    ? extractLegacyLinksFromContentBlocks(getEntityContentBlocks(entity, entityType))
    : [];

  return mergeLinkListings(registryLinks, legacyLinks);
}

function isEntityLinkedInListing(links, entityType, entityId) {
  return links.some((link) => link.type === entityType && link.id === entityId);
}

export const entityLinkingService = {
  /**
   * Checks if a link already exists between two entities (either direction)
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID
   * @param {string} sourceEntityType - Type of source entity
   * @param {string} sourceEntityId - ID of source entity
   * @param {string} targetEntityType - Type of target entity
   * @param {string} targetEntityId - ID of target entity
   * @returns {Promise<Object>} { exists: boolean, direction: 'source->target' | 'target->source' | 'both' | null }
   */
  async checkLinkExists(userId, tripId, sourceEntityType, sourceEntityId, targetEntityType, targetEntityId) {
    try {
      const config = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
      if (shouldReadFromRegistry(config)) {
        const registryEndpoints = await resolveRegistryLinkEndpoints(
          userId,
          tripId,
          sourceEntityType,
          sourceEntityId
        );
        if (isEntityLinkedInListing(registryEndpoints, targetEntityType, targetEntityId)) {
          return { exists: true, direction: 'both' };
        }
        if (config.readMode === 'registry_only') {
          return { exists: false, direction: null };
        }
      }

      return checkLegacyContentLinkExists(
        userId,
        tripId,
        sourceEntityType,
        sourceEntityId,
        targetEntityType,
        targetEntityId
      );
    } catch (error) {
      console.error('Error checking link existence:', error);
      throw error;
    }
  },

  /**
   * Creates a bidirectional link between two entities
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID
   * @param {string} sourceEntityType - Type of source entity
   * @param {string} sourceEntityId - ID of source entity
   * @param {string} targetEntityType - Type of target entity
   * @param {string} targetEntityId - ID of target entity
   * @param {Object} options - Optional configuration
   * @param {boolean} options.bidirectional - Whether to create bidirectional link (default: true)
   * @param {Object} options.metadata - Additional metadata for content blocks
   * @returns {Promise<Object>} Result object with link details
   */
  async createLink(userId, tripId, sourceEntityType, sourceEntityId, targetEntityType, targetEntityId, options = {}) {
    const config = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
    if (shouldRouteThroughRegistry(config)) {
      const linkType = resolveManualLinkType(sourceEntityType, targetEntityType);
      const result = await connectionRegistryService.createLink(
        userId,
        tripId,
        { type: sourceEntityType, id: sourceEntityId },
        { type: targetEntityType, id: targetEntityId },
        {
          linkType,
          linkIntent: options.linkIntent || 'selected',
          displayMode: options.displayMode || 'related_only',
          displayContext: options.metadata?.context || null,
          metadata: {
            createdBy: 'user',
            connectionType: options.metadata?.connectionType || 'related'
          },
          skipLegacyWrite: config.writeMode === 'registry_only'
        }
      );

      eventBridgeService.publish(EVENT_TYPES.ENTITY_LINKED, {
        userId,
        tripId,
        sourceEntityType,
        sourceEntityId,
        targetEntityType,
        targetEntityId,
        bidirectional: options.bidirectional !== false,
        linkId: result.linkId,
        timestamp: new Date().toISOString()
      });

      if (config.writeMode === 'registry_only') {
        const registryLinks = await connectionRegistryService.getLinksBetween(
          userId,
          tripId,
          { type: sourceEntityType, id: sourceEntityId },
          { type: targetEntityType, id: targetEntityId },
          linkType
        );
        if (registryLinks[0]) {
          await legacyLinkAdapter.stripLegacyShadow(userId, tripId, registryLinks[0]);
        }
      }

      return {
        success: true,
        linkId: result.linkId,
        deduplicated: result.deduplicated,
        bidirectional: options.bidirectional !== false
      };
    }

    return this.createLegacyLinkOnly(
      userId,
      tripId,
      sourceEntityType,
      sourceEntityId,
      targetEntityType,
      targetEntityId,
      options
    );
  },

  /**
   * Creates legacy embedded references only (no registry write).
   */
  async createLegacyLinkOnly(userId, tripId, sourceEntityType, sourceEntityId, targetEntityType, targetEntityId, options = {}) {
    const { bidirectional = true, metadata = {} } = options;

    try {
      const linkCheck = await checkLegacyContentLinkExists(
        userId,
        tripId,
        sourceEntityType,
        sourceEntityId,
        targetEntityType,
        targetEntityId
      );

      if (linkCheck.exists && linkCheck.direction === 'both') {
        return {
          success: true,
          bidirectional,
          alreadyExists: true
        };
      }

      // Get both entities
      const sourceEntity = await getEntityById(userId, tripId, sourceEntityType, sourceEntityId);
      const targetEntity = await getEntityById(userId, tripId, targetEntityType, targetEntityId);

      if (!sourceEntity) {
        throw new Error(`Source entity ${sourceEntityType}/${sourceEntityId} not found`);
      }
      if (!targetEntity) {
        throw new Error(`Target entity ${targetEntityType}/${targetEntityId} not found`);
      }

      const linkMetadata = {
        sourceContentBlockId: null,
        targetContentBlockId: null
      };

      // Create source -> target link
      // Add content block to source entity (or connection if source is a document)
      if (sourceEntityType === 'document') {
        if (!hasDocumentConnection(sourceEntity, targetEntityType, targetEntityId)) {
          await addDocumentConnection(
            userId,
            tripId,
            sourceEntityId,
            targetEntityType,
            targetEntityId,
            metadata.connectionType || 'related'
          );
          linkMetadata.sourceContentBlockId = `connection_${targetEntityType}_${targetEntityId}`;
        }
      } else {
        const sourceContentBlocks = getEntityContentBlocks(sourceEntity, sourceEntityType);
        if (!hasEntityReference(sourceContentBlocks, targetEntityType, targetEntityId)) {
          const sourceReferenceBlock = createReferenceBlock(sourceEntityType, targetEntityType, targetEntity, metadata);
          linkMetadata.sourceContentBlockId = sourceReferenceBlock.id;

          const updatedSourceBlocks = [...sourceContentBlocks, sourceReferenceBlock];
          await updateEntityContentBlocks(userId, tripId, sourceEntityType, sourceEntityId, updatedSourceBlocks);
        }
      }

      // Create target -> source link (bidirectional)
      // Add connection/content block to target entity
      if (bidirectional) {
        if (targetEntityType === 'document') {
          // If target is a document, add connection to document
          if (!hasDocumentConnection(targetEntity, sourceEntityType, sourceEntityId)) {
            await addDocumentConnection(
              userId,
              tripId,
              targetEntityId,
              sourceEntityType,
              sourceEntityId,
              metadata.connectionType || 'related'
            );
            linkMetadata.targetContentBlockId = `connection_${sourceEntityType}_${sourceEntityId}`;
          }
        } else {
          // Normal content block handling - add content block to target
          const targetContentBlocks = getEntityContentBlocks(targetEntity, targetEntityType);

          if (!hasEntityReference(targetContentBlocks, sourceEntityType, sourceEntityId)) {
            // Create a copy of metadata for the reciprocal link and remove entityName
            // so that createReferenceBlock uses the source entity's name instead of the target entity's name
            const reciprocalMetadata = { ...metadata };
            delete reciprocalMetadata.entityName;

            const targetReferenceBlock = createReferenceBlock(targetEntityType, sourceEntityType, sourceEntity, reciprocalMetadata);

            linkMetadata.targetContentBlockId = targetReferenceBlock.id;

            const updatedTargetBlocks = [...targetContentBlocks, targetReferenceBlock];

            await updateEntityContentBlocks(userId, tripId, targetEntityType, targetEntityId, updatedTargetBlocks);
          }
        }
      }

      // Publish event
      eventBridgeService.publish(EVENT_TYPES.ENTITY_LINKED, {
        userId,
        tripId,
        sourceEntityType,
        sourceEntityId,
        targetEntityType,
        targetEntityId,
        bidirectional,
        timestamp: new Date().toISOString(),
        linkMetadata
      });

      return {
        success: true,
        sourceContentBlockId: linkMetadata.sourceContentBlockId,
        targetContentBlockId: linkMetadata.targetContentBlockId,
        bidirectional
      };
    } catch (error) {
      console.error('Error creating link:', error);
      throw error;
    }
  },

  /**
   * Removes a link between two entities (both directions)
   * Handles orphaned references gracefully - if target entity doesn't exist,
   * still cleans up the source entity's content blocks.
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID
   * @param {string} sourceEntityType - Type of source entity
   * @param {string} sourceEntityId - ID of source entity
   * @param {string} targetEntityType - Type of target entity
   * @param {string} targetEntityId - ID of target entity
   * @returns {Promise<Object>} Result object
   */
  async removeLink(userId, tripId, sourceEntityType, sourceEntityId, targetEntityType, targetEntityId) {
    const config = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
    if (shouldRouteThroughRegistry(config)) {
      // Remove EVERY active link between this pair, regardless of type — a pair can
      // carry both a typed link (stays_on/located_in, from migration or a suggestion)
      // and a leftover associated_with (from Link Entities or extraction). Chip X must
      // clear all of them, not just whichever type resolveManualLinkType would pick
      // for a *new* link (that hard-coding is why unlink could report success while
      // the real link — e.g. stays_on — survived).
      const sourceLinks = await connectionRegistryService.getLinksForEntity(
        userId,
        tripId,
        sourceEntityType,
        sourceEntityId
      );
      const matchingLinks = sourceLinks.filter((link) => {
        const other = getOtherEndpoint(link, sourceEntityType, sourceEntityId);
        return other && other.type === targetEntityType && other.id === targetEntityId;
      });

      for (const link of matchingLinks) {
        await connectionRegistryService.removeLink(userId, tripId, link.linkId || link.id);
      }

      if (config.writeMode === 'registry_only') {
        for (const link of matchingLinks) {
          await legacyLinkAdapter.stripLegacyShadow(userId, tripId, link);
        }
      }

      // Also clean up any surviving legacy content-block reference for this pair —
      // including on registry_only trips (N2). A chip that only exists because of a
      // leftover legacy block (no registry row behind it) previously could never be
      // removed: the registry branch found nothing, and removeLegacyLinkOnly refused
      // to touch registry_only trips at all. This is cleanup of stray data left by
      // extraction, not a new legacy write, so it is safe to allow here.
      const legacyResult = await this.removeLegacyLinkOnly(
        userId,
        tripId,
        sourceEntityType,
        sourceEntityId,
        targetEntityType,
        targetEntityId,
        { suppressEvent: true, allowOnRegistryOnly: true }
      );

      eventBridgeService.publish(EVENT_TYPES.ENTITY_UNLINKED, {
        userId,
        tripId,
        sourceEntityType,
        sourceEntityId,
        targetEntityType,
        targetEntityId,
        registryLinksRemoved: matchingLinks.length,
        legacyBlocksRemoved: Boolean(legacyResult?.removedFromSource || legacyResult?.removedFromTarget),
        timestamp: new Date().toISOString()
      });

      return { success: true, registryLinksRemoved: matchingLinks.length };
    }

    return this.removeLegacyLinkOnly(
      userId,
      tripId,
      sourceEntityType,
      sourceEntityId,
      targetEntityType,
      targetEntityId
    );
  },

  /**
   * Removes legacy embedded references only (no registry write).
   */
  async removeLegacyLinkOnly(
    userId,
    tripId,
    sourceEntityType,
    sourceEntityId,
    targetEntityType,
    targetEntityId,
    options = {}
  ) {
    const { suppressEvent = false, allowOnRegistryOnly = false } = options;

    try {
      // Get source entity (must exist)
      const sourceEntity = await getEntityById(userId, tripId, sourceEntityType, sourceEntityId);

      if (!sourceEntity) {
        throw new Error('Source entity not found');
      }

      const config = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
      if (config.writeMode === 'registry_only' && !allowOnRegistryOnly) {
        return { success: true, skippedLegacy: true };
      }

      let removedFromSource = false;
      let removedFromTarget = false;

      // Remove source -> target link
      if (sourceEntityType === 'document') {
        await documentService.removeConnection(userId, tripId, sourceEntityId, targetEntityType, targetEntityId, {
          legacyCleanupOnly: true,
          suppressEvent: true
        });
      } else {
        const sourceContentBlocks = getEntityContentBlocks(sourceEntity, sourceEntityType);
        const updatedSourceBlocks = removeEntityReference(sourceContentBlocks, targetEntityType, targetEntityId);
        if (updatedSourceBlocks.length !== sourceContentBlocks.length) {
          await updateEntityContentBlocks(userId, tripId, sourceEntityType, sourceEntityId, updatedSourceBlocks);
          removedFromSource = true;
        }
      }

      const targetEntity = await getEntityById(userId, tripId, targetEntityType, targetEntityId);

      if (targetEntity) {
        if (targetEntityType === 'document') {
          await documentService.removeConnection(userId, tripId, targetEntityId, sourceEntityType, sourceEntityId, {
            legacyCleanupOnly: true,
            suppressEvent: true
          });
        } else {
          const targetContentBlocks = getEntityContentBlocks(targetEntity, targetEntityType);
          const updatedTargetBlocks = removeEntityReference(targetContentBlocks, sourceEntityType, sourceEntityId);
          if (updatedTargetBlocks.length !== targetContentBlocks.length) {
            await updateEntityContentBlocks(userId, tripId, targetEntityType, targetEntityId, updatedTargetBlocks);
            removedFromTarget = true;
          }
        }
      }

      if (!suppressEvent) {
        eventBridgeService.publish(EVENT_TYPES.ENTITY_UNLINKED, {
          userId,
          tripId,
          sourceEntityType,
          sourceEntityId,
          targetEntityType,
          targetEntityId,
          targetEntityMissing: !targetEntity,
          timestamp: new Date().toISOString()
        });
      }

      return { success: true, targetEntityMissing: !targetEntity, removedFromSource, removedFromTarget };
    } catch (error) {
      console.error('Error removing link:', error);
      throw error;
    }
  },

  /**
   * Gets all links for a specific entity
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID
   * @param {string} entityType - Type of entity
   * @param {string} entityId - ID of entity
   * @returns {Promise<Array>} Array of link objects
   */
  async getEntityLinks(userId, tripId, entityType, entityId) {
    try {
      return await resolveEntityLinkListing(userId, tripId, entityType, entityId);
    } catch (error) {
      console.error('Error getting entity links:', error);
      throw error;
    }
  },

  /**
   * Gets linkable entities, filtering out already linked ones
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID
   * @param {string} sourceEntityType - Type of source entity
   * @param {string} sourceEntityId - ID of source entity
   * @param {Array<string>} targetEntityTypes - Array of target entity types to fetch
   * @returns {Promise<Object>} Object with arrays of linkable entities by type
   */
  async getLinkableEntities(userId, tripId, sourceEntityType, sourceEntityId, targetEntityTypes) {
    try {
      // Get source entity to check existing links
      const sourceEntity = await getEntityById(userId, tripId, sourceEntityType, sourceEntityId);
      if (!sourceEntity) {
        throw new Error(`Source entity ${sourceEntityType}/${sourceEntityId} not found`);
      }

      const existingLinks = await resolveEntityLinkListing(userId, tripId, sourceEntityType, sourceEntityId);
      const result = {};

      // Fetch all entities of requested types
      const fetchPromises = targetEntityTypes.map(async (entityType) => {
        let entities = [];
        switch (entityType) {
        case 'day':
          entities = await daysService.fetchDays(userId, tripId);
          break;
        case 'accommodation':
          entities = await accommodationService.fetchAccommodation(userId, tripId);
          break;
        case 'place':
          entities = await placesService.fetchPlaces(userId, tripId);
          break;
        case 'travel':
          entities = await travelService.fetchTravel(userId, tripId);
          break;
        case 'document':
          entities = await documentService.fetchDocuments(userId, tripId);
          break;
        default:
          console.warn(`Unknown entity type: ${entityType}`);
        }
        return { type: entityType, entities };
      });

      const fetchedData = await Promise.all(fetchPromises);

      // Filter out already linked entities
      fetchedData.forEach(({ type, entities }) => {
        result[type] = entities.filter((entity) => {
          const entityId = type === 'document' ? (entity.firestoreId || entity.id) : entity.id;
          return !isEntityLinkedInListing(existingLinks, type, entityId);
        });
      });

      return result;
    } catch (error) {
      console.error('Error getting linkable entities:', error);
      throw error;
    }
  }
};
